'use client';

import { cn } from '@/lib/utils';
import clsx from 'clsx';

const boundaryCount = 1;
const siblingCount = 1;
const showFirstButton = false;
const showLastButton = false;
const hideNextButton = false;
const hidePrevButton = false;

const range = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from(
    {
      length,
    },
    (_, i) => start + i,
  );
};

interface Props {
  currentPage: number;
  totalPages: number;
  className?: string;
  onChange: (nextPage: number) => void;
}

const BtnPagination = (props: Props) => {
  const { currentPage, totalPages, className, onChange } = props;

  const numericTotalPages = typeof totalPages === 'number' ? totalPages : parseInt(totalPages);
  const numericCurrentPage = typeof currentPage === 'number' ? currentPage : parseInt(currentPage);

  const isActiveLeft = numericCurrentPage > 1;
  const isActiveRight = numericCurrentPage < numericTotalPages;

  const tiles = (() => {
    const startPages = range(1, Math.min(boundaryCount, totalPages));
    const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);

    const siblingsStart = Math.max(
      Math.min(currentPage - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
      boundaryCount + 2,
    );

    const siblingsEnd = Math.min(
      Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
      endPages.length > 0 ? endPages[0] - 2 : totalPages - 1,
    );

    const tiles = [
      ...(showFirstButton ? ['first'] : []),
      ...(hidePrevButton ? [] : ['previous']),
      ...startPages,

      ...(siblingsStart > boundaryCount + 2
        ? ['start-ellipsis']
        : boundaryCount + 1 < totalPages - boundaryCount
          ? [boundaryCount + 1]
          : []),

      ...range(siblingsStart, siblingsEnd),

      ...(siblingsEnd < totalPages - boundaryCount - 1
        ? ['end-ellipsis']
        : totalPages - boundaryCount > boundaryCount
          ? [totalPages - boundaryCount]
          : []),

      ...endPages,
      ...(hideNextButton ? [] : ['next']),
      ...(showLastButton ? ['last'] : []),
    ];

    return tiles;
  })();

  if (!numericTotalPages || numericTotalPages <= 1) {
    return null;
  }

  return (
    <div className={cn(' flex items-center justify-center gap-x-2.5 ', className)}>
      {tiles.map(tileNumber => {
        if (tileNumber === 'previous') {
          if (!isActiveLeft) {
            return (
              <div
                key={tileNumber}
                className='flex h-[25px] w-[25px] items-center justify-center'
              >
                {leftArrow}
              </div>
            );
          }

          return (
            <button
              className='flex h-[25px] w-[25px] items-center justify-center'
              aria-label='prev-page'
              key={tileNumber}
              type='button'
              onClick={() => {
                onChange(currentPage - 1);
              }}
            >
              {leftArrow}
            </button>
          );
        }

        if (tileNumber === 'next') {
          if (!isActiveRight) {
            return (
              <div
                key={tileNumber}
                className='flex h-[25px] w-[25px] items-center justify-center'
              >
                {rightArrow}
              </div>
            );
          }

          return (
            <button
              className='flex h-[25px] w-[25px] items-center justify-center'
              aria-label='next-page'
              key={tileNumber}
              type='button'
              onClick={() => {
                onChange(currentPage + 1);
              }}
            >
              {rightArrow}
            </button>
          );
        }

        if (tileNumber === 'start-ellipsis' || tileNumber === 'end-ellipsis') {
          return (
            <div
              key={tileNumber}
              className='h-[25px] w-[25px]'
            >
              ...
            </div>
          );
        }

        return (
          <button
            className={clsx(
              'flex h-[25px] w-[25px] items-center justify-center',
              String(currentPage) === String(tileNumber) ? 'border-1 border-solid border-80583F text-80583F' : '',
              'hover_text-80583F',
            )}
            type='button'
            aria-label={`page-${tileNumber}`}
            key={tileNumber}
            onClick={() => {
              onChange(typeof tileNumber === 'string' ? parseInt(tileNumber) : tileNumber);
            }}
          >
            {tileNumber}
          </button>
        );
      })}
    </div>
  );
};

const leftArrow = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='6'
    height='11'
    fill='none'
    viewBox='0 0 6 11'
  >
    <path
      stroke='#80583F'
      strokeLinecap='round'
      strokeLinejoin='round'
      d='M5.25 1 .75 5.5l4.5 4.5'
    ></path>
  </svg>
);
const rightArrow = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='6'
    height='11'
    fill='none'
    viewBox='0 0 6 11'
  >
    <path
      stroke='#80583F'
      strokeLinecap='round'
      strokeLinejoin='round'
      d='m.75 10 4.5-4.5L.75 1'
    ></path>
  </svg>
);
export default BtnPagination;
